/**
 * Conversation Chat Component - Chat interface for agent conversations
 */

import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  Input,
  Button,
  Space,
  Typography,
  Tag,
  Avatar,
  Divider,
  Select,
  message,
  Popconfirm,
} from "antd";
import {
  SendOutlined,
  UserOutlined,
  RobotOutlined,
  CloseOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import { useAgent } from "../context/AgentContext.jsx";
import dayjs from "dayjs";
import {
  MessagesScroll,
  MessageRow,
  Bubble,
  Meta,
} from "./ConversationChat.styled";

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Option } = Select;

const ConversationChat = ({ conversation, onClose }) => {
  const {
    currentConversation,
    sendMessage,
    closeConversation,
    assignConversation,
  } = useAgent();

  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);
  const [agentId, setAgentId] = useState("admin"); // Default agent ID
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentConversation?.messages]);

  // Handle send message
  const handleSendMessage = async () => {
    if (!messageText.trim() || !currentConversation) return;

    setSending(true);
    try {
      const result = await sendMessage(currentConversation.id, {
        content: messageText.trim(),
        agentId,
        messageType: "text",
      });

      if (result.success) {
        setMessageText("");
        message.success("Message sent successfully");
      } else {
        message.error(result.error || "Failed to send message");
      }
    } catch (error) {
      message.error("Error sending message");
    } finally {
      setSending(false);
    }
  };

  // Handle close conversation
  const handleCloseConversation = async () => {
    if (!currentConversation) return;

    try {
      const result = await closeConversation(currentConversation.id, agentId);
      if (result.success) {
        message.success("Conversation closed");
        onClose();
      } else {
        message.error(result.error || "Failed to close conversation");
      }
    } catch (error) {
      message.error("Error closing conversation");
    }
  };

  // Handle assign conversation
  const handleAssignConversation = async (newAgentId) => {
    if (!currentConversation) return;

    try {
      const result = await assignConversation(
        currentConversation.id,
        newAgentId
      );
      if (result.success) {
        setAgentId(newAgentId);
        message.success("Conversation assigned successfully");
      } else {
        message.error(result.error || "Failed to assign conversation");
      }
    } catch (error) {
      message.error("Error assigning conversation");
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!currentConversation) {
    return (
      <Card style={{ height: "100%" }}>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Text type="secondary">Select a conversation to start chatting</Text>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <div>{currentConversation.conversation.customerPhone}</div>
            <div style={{ fontSize: "12px", color: "#666" }}>
              {currentConversation.conversation.status === "pending" && (
                <Tag color="orange">Pending</Tag>
              )}
              {currentConversation.conversation.status === "active" && (
                <Tag color="green">Active</Tag>
              )}
              {currentConversation.conversation.status === "closed" && (
                <Tag color="red">Closed</Tag>
              )}
              {currentConversation.conversation.agentId && (
                <Tag color="purple">
                  Agent: {currentConversation.conversation.agentId}
                </Tag>
              )}
            </div>
          </div>
        </Space>
      }
      extra={
        <Space>
          <Select
            value={agentId}
            onChange={handleAssignConversation}
            style={{ width: 120 }}
            placeholder="Assign to"
          >
            <Option value="admin">Admin</Option>
            <Option value="agent1">Agent 1</Option>
            <Option value="agent2">Agent 2</Option>
          </Select>
          {currentConversation.conversation.status !== "closed" && (
            <Popconfirm
              title="Close conversation?"
              description="This will end the conversation with the customer."
              onConfirm={handleCloseConversation}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="primary"
                danger
                icon={<CloseOutlined />}
                size="small"
              >
                Close
              </Button>
            </Popconfirm>
          )}
          <Button icon={<CloseOutlined />} onClick={onClose} size="small">
            Back
          </Button>
        </Space>
      }
      style={{ height: "100%" }}
      bodyStyle={{ padding: 0, display: "flex", flexDirection: "column" }}
    >
      {/* Messages */}
      <MessagesScroll role="log" aria-live="polite">
        {currentConversation.messages?.map((msg) => (
          <MessageRow
            key={msg.id}
            $align={msg.senderType === "customer" ? "start" : "end"}
          >
            <Bubble
              $variant={msg.senderType === "customer" ? "customer" : "agent"}
            >
              <div style={{ marginBottom: 4 }}>
                <Text
                  style={{
                    fontSize: 12,
                    color: msg.senderType === "customer" ? "#666" : "#fff",
                  }}
                >
                  {msg.senderType === "customer" ? "Customer" : "Agent"}
                </Text>
              </div>
              <div>{msg.content}</div>
              <Meta>{dayjs(msg.timestamp).format("HH:mm")}</Meta>
            </Bubble>
          </MessageRow>
        ))}
        <div ref={messagesEndRef} />
      </MessagesScroll>

      {/* Message input */}
      {currentConversation.conversation.status !== "closed" && (
        <>
          <Divider style={{ margin: "0 16px" }} />
          <div style={{ padding: "16px" }}>
            <Space.Compact style={{ width: "100%" }}>
              <TextArea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                autoSize={{ minRows: 2, maxRows: 4 }}
                style={{ flex: 1 }}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSendMessage}
                loading={sending}
                disabled={!messageText.trim()}
              >
                Send
              </Button>
            </Space.Compact>
          </div>
        </>
      )}
    </Card>
  );
};

export default ConversationChat;
